import { useEffect, useRef, useState } from "react";
import { CalendarClock } from "lucide-react";

const CHOICES = [["after_school", "Après l’école", "16 h-18 h"], ["evening", "En soirée", "18 h-21 h"], ["custom", "Choisir mes heures", ""]];
const TIMES = [];
for (let h = 6; h <= 22; h += 1) ["00", "20", "40"].forEach((m) => TIMES.push(`${String(h).padStart(2, "0")}:${m}`));
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", month: "short" });

/** Flow 20 (déterministe): availability -> proposal -> Confirmer / Modifier / Reporter. Nothing saved before Confirmer. */
const ScheduleProposalCard = ({ api, studentId, remembered = null, mobileType = false, time, onProposal, onConfirmed, onEdit, onLater }) => {
  const [proposal, setProposal] = useState(null);
  const [custom, setCustom] = useState({ start: "16:00", end: "18:00", open: false });
  const [state, setState] = useState("ask");
  const [error, setError] = useState("");
  const autoRan = useRef(false);
  const size = mobileType ? "text-[15px]" : "text-[13px]";
  const ghost = `rounded-full border border-gray-200 px-2 py-2 font-semibold text-[#2d5a3d] ${size}`;

  const ask = async (availability) => {
    setError("");
    try {
      const data = await api.proposeSchedule(studentId, availability === "custom" ? { availability, start: custom.start, end: custom.end } : { availability });
      setProposal(data);
      setState("proposal");
      onProposal?.(data);
    } catch (problem) {
      setError(problem.message || "Proposition impossible.");
    }
  };

  // Remembered availability: go straight to the proposed week (Flow 4 style).
  useEffect(() => {
    if (!remembered?.kind || autoRan.current) return;
    autoRan.current = true; // StrictMode runs effects twice in dev: propose only once.
    if (remembered.kind === "custom") setCustom((c) => ({ ...c, start: remembered.start || c.start, end: remembered.end || c.end }));
    setError("");
    api.proposeSchedule(studentId, remembered.kind === "custom" ? { availability: "custom", start: remembered.start, end: remembered.end } : { availability: remembered.kind })
      .then((data) => { setProposal(data); setState("proposal"); onProposal?.(data); })
      .catch((problem) => setError(problem.message || "Proposition impossible."));
    // Runs once for the remembered choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    setState("saving");
    try {
      const data = await api.confirmSchedule(studentId, { proposalId: proposal.proposalId, slots: proposal.slots });
      setState("done");
      onConfirmed?.(data);
    } catch (problem) {
      setError(problem.message || "Enregistrement impossible.");
      setState("proposal");
    }
  };

  return (
    <div className="w-[94%] sm:w-[88%] max-w-[23rem] self-start rounded-2xl rounded-tl-sm bg-white p-2.5 sm:p-3 shadow-sm" data-testid="schedule-card">
      <p className={`flex items-center gap-1.5 font-semibold text-[#122118] ${size}`}><CalendarClock className="h-4 w-4 text-[#189346]" />Horaire proposé</p>
      {state === "ask" && (
        <div className="mt-2 grid gap-1.5">
          {CHOICES.map(([id, label, hint]) => (
            <button key={id} type="button" onClick={() => (id === "custom" ? setCustom((c) => ({ ...c, open: true })) : ask(id))} className={`${ghost} text-left`} data-testid={`schedule-avail-${id}`}>
              {label} {hint && <span className="text-[11px] font-normal text-gray-500">· {hint}</span>}
            </button>
          ))}
          <button type="button" onClick={() => { setState("closed"); onLater?.("skip"); }} className={`${ghost} text-left text-gray-500`} data-testid="schedule-skip">Plus tard</button>
          {custom.open && (
            <div className="flex items-center gap-1.5">
              {["start", "end"].map((key) => (
                <select key={key} value={custom[key]} onChange={(e) => setCustom((c) => ({ ...c, [key]: e.target.value }))} className={`rounded-lg border border-gray-200 px-2 py-1 ${size}`} data-testid={`schedule-custom-${key}`}>
                  {TIMES.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              ))}
              <button type="button" onClick={() => ask("custom")} className={`rounded-full bg-[#25D366] px-3 py-1.5 font-semibold ${size}`} data-testid="schedule-custom-go">OK</button>
            </div>
          )}
        </div>
      )}
      {proposal && state !== "ask" && (
        <div className="mt-2 space-y-1" data-testid="schedule-slots">
          {proposal.slots.map((slot) => (
            <p key={`${slot.planId}-${slot.date}-${slot.time}`} className={`rounded-lg bg-[#F3FCF7] px-2 py-1 ${size}`} data-testid="schedule-slot">
              <b>{dayLabel(slot.date)} · {slot.time}</b> — {slot.planTitle} <span className="text-[11px] text-gray-500">({20 * (slot.blocks || 1)} min)</span>
            </p>
          ))}
          {!proposal.slots.length && <p className="text-xs text-gray-500">Rien à placer avec ces disponibilités.</p>}
          {proposal.unplaced.map((row) => <p key={row.planId} className="text-[11px] text-[#8a5a00]" data-testid="schedule-unplaced">{row.title} : {row.count} séance(s) non placée(s). {row.reason}</p>)}
        </div>
      )}
      {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="schedule-error">{error}</p>}
      {state === "proposal" && (
        <>
          <button type="button" onClick={() => setState("ask")} className="mt-2 text-[12px] font-semibold text-[#189346] underline" data-testid="schedule-change-avail">Changer mes disponibilités</button>
          {proposal.slots.length > 0 && <button type="button" onClick={confirm} className={`mt-3 w-full rounded-full bg-[#25D366] px-3 py-2 font-semibold text-[#0a2914] ${size}`} data-testid="schedule-confirm">Confirmer</button>}
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <button type="button" onClick={() => { setState("closed"); onEdit?.(); }} className={ghost} data-testid="schedule-edit">Modifier</button>
            <button type="button" onClick={() => { setState("closed"); onLater?.(); }} className={ghost} data-testid="schedule-later">Reporter</button>
          </div>
        </>
      )}
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </div>
  );
};

export default ScheduleProposalCard;
